<template>
  <a-layout class="layout">
    <a-layout-sider
      class="height: 100vh"
      width="250px"
      :trigger="null"
      collapsible
    >
      <a-menu
        v-model:selectedKeys="state.selectedKeys"
        mode="inline"
        theme="dark"
        :open-keys="state.openKeys"
        :items="items"
        @openChange="onOpenChange"
      />
    </a-layout-sider>
    <a-layout>
      <a-layout-header class="header" style="background: #fff; padding: 0">
        <div class="header-content">
          <a-dropdown class="dropdown-right">
            <template #overlay>
              <a-menu>
                <a-menu-item @click="handleRouter('/')">
                  <HddOutlined />
                  系统主页
                </a-menu-item>
                <a-menu-item @click="handleRouter('/login')">
                  <UserOutlined />
                  退出系统
                </a-menu-item>
              </a-menu>
            </template>
            <a-button class="menu-button">
              菜单
              <DownOutlined />
            </a-button>
          </a-dropdown>
        </div>
      </a-layout-header>
      <a-layout-content style="background: #ececec">
        <router-view />
      </a-layout-content>
    </a-layout>
  </a-layout>
</template>

<script setup lang="ts">
import {
  HomeOutlined,
  SettingOutlined,
  UserOutlined,
  ShoppingOutlined,
  ProjectOutlined,
  HddOutlined,
  LoginOutlined,
  DownOutlined,
} from "@ant-design/icons-vue";
import { reactive, h, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";

const router = useRouter();

const handleRouter = (path: string) => {
  router.push({ path: path, replace: true });
};

const route = useRoute();

interface MenuItemType {
  key: string;
  label: string | ReturnType<typeof h>;
  icon?: ReturnType<typeof h>;
  children?: MenuItemType[];
}

interface SubMenuType {
  key: string;
  label: string | ReturnType<typeof h>;
  icon?: ReturnType<typeof h>;
  children: MenuItemType[];
}

type ItemType = MenuItemType | SubMenuType;

// 定义菜单项生成函数
function getItem(
  label: string | ReturnType<typeof h>,
  key: string,
  icon?: ReturnType<typeof h>,
  children?: ItemType[]
): ItemType {
  return {
    key,
    icon,
    children,
    label,
  } as ItemType;
}

// 菜单项数据
const items: ItemType[] = reactive([
  getItem(h("a", { href: "/", target: "_self" }, "主页"), "/", h(HomeOutlined)),
  getItem("常规操作", "2", h(SettingOutlined), [
    getItem(
      h("a", { href: "/system", target: "_self" }, "系统配置"),
      "/system"
    ),
    getItem(
      h("a", { href: "/system/pay", target: "_self" }, "支付配置"),
      "/system/pay"
    ),
    getItem(
      h("a", { href: "/system/carousel", target: "_self" }, "轮播管理"),
      "/system/carousel"
    ),
    getItem(
      h("a", { href: "/system/template", target: "_self" }, "模版管理"),
      "/system/template"
    ),
    getItem(
      h("a", { href: "/system/mail", target: "_self" }, "快递配置"),
      "/system/mail"
    ),
    getItem(
      h("a", { href: "/system/recharge", target: "_self" }, "储值活动"),
      "/system/recharge"
    ),
    getItem(
      h("a", { href: "/system/postal", target: "_self" }, "邮编管理"),
      "/system/postal"
    ),
    getItem(
      h("a", { href: "/system/supervisor", target: "_self" }, "监所管理"),
      "/system/supervisor"
    ),
  ]),
  getItem("会员管理", "3", h(UserOutlined), [
    getItem(
      h("a", { href: "/admin/user", target: "_self" }, "所有会员"),
      "/admin/user"
    ),
    getItem(
      h("a", { href: "/admin", target: "_self" }, "所有管理员"),
      "/admin"
    ),
    getItem(
      h("a", { href: "/user/recharge", target: "_self" }, "在线储值记录"),
      "/user/recharge"
    ),
    getItem(
      h("a", { href: "/user/reply", target: "_self" }, "回信管理"),
      "/user/reply"
    ),
    getItem(
      h("a", { href: "/user/address", target: "_self" }, "收寄地址"),
      "/user/address"
    ),
  ]),
  getItem("商品管理", "4", h(ShoppingOutlined), [
    getItem(
      h("a", { href: "/product/pending", target: "_self" }, "待审核"),
      "/product/pending"
    ),
    getItem(
      h("a", { href: "/product/active", target: "_self" }, "出售中"),
      "/product/active"
    ),
    getItem(
      h("a", { href: "/product/stock", target: "_self" }, "仓库中"),
      "/product/stock"
    ),
    getItem(
      h("a", { href: "/product/inactive", target: "_self" }, "下架"),
      "/product/inactive"
    ),
    getItem(
      h("a", { href: "/product/category", target: "_self" }, "商品分类"),
      "/product/category"
    ),
  ]),
  getItem("订单管理", "5", h(ProjectOutlined), [
    getItem(
      h("a", { href: "/order/pending", target: "_self" }, "待支付"),
      "/order/pending"
    ),
    getItem(
      h("a", { href: "/order/paid", target: "_self" }, "已支付"),
      "/order/paid"
    ),
    getItem(
      h("a", { href: "/order/processed", target: "_self" }, "已制作"),
      "/order/processed"
    ),
    getItem(
      h("a", { href: "/order/shipped", target: "_self" }, "已寄出"),
      "/order/shipped"
    ),
    getItem(
      h("a", { href: "/order/canceled", target: "_self" }, "回收站"),
      "/order/canceled"
    ),
    getItem(
      h("a", { href: "/order/withdrawal", target: "_self" }, "余额提现"),
      "/order/withdrawal"
    ),
  ]),
  getItem("日记存稿", "6", h(HddOutlined), [
    getItem(
      h("a", { href: "/journal", target: "_self" }, "会员日记"),
      "/journal"
    ),
  ]),
  getItem("退出系统", "7", h(LoginOutlined), [
    getItem(h("a", { href: "/login", target: "_self" }, "退出系统"), "/logout"),
  ]),
]);

// 菜单状态
const state = reactive({
  rootSubmenuKeys: ["1", "2", "3", "4", "5", "6", "7"], // 所有父菜单的 key
  openKeys: [] as string[], // 当前展开的父菜单
  selectedKeys: [route.path], // 当前选中的菜单项
});

// 查找当前路由对应的父菜单
function findParentKey(key: string): string | undefined {
  for (const item of items) {
    if (item.children) {
      const child = item.children.find((child) => child.key === key);
      if (child) {
        return item.key as string;
      }
    }
  }
  return undefined;
}

// 初始化时打开父菜单
onMounted(() => {
  const parentKey = findParentKey(route.path);
  if (parentKey) {
    state.openKeys = [parentKey];
  }
});

// 处理父菜单展开逻辑
const onOpenChange = (openKeys: string[]) => {
  const latestOpenKey = openKeys.find((key) => !state.openKeys.includes(key));
  if (state.rootSubmenuKeys.indexOf(latestOpenKey!) === -1) {
    state.openKeys = openKeys;
  } else {
    state.openKeys = latestOpenKey ? [latestOpenKey] : [];
  }
};
</script>

<style scoped>
.layout {
  height: 100vh;
}

.header {
  position: relative;
  height: 64px; /* 确保header有固定高度 */
  line-height: 64px; /* 与高度一致以辅助垂直居中 */
}

.header-content {
  display: flex;
  justify-content: flex-end; /* 水平靠右 */
  align-items: center; /* 垂直居中 */
  height: 100%; /* 填充整个header高度 */
  padding: 0 24px; /* 左右内边距 */
}

.dropdown-right {
  display: flex;
  align-items: center; /* 确保dropdown内部元素也垂直居中 */
}

.menu-button {
  display: flex;
  align-items: center; /* 按钮内部文字和图标垂直居中 */
}
</style>
